import log from 'loglevel';

import { getApiAddress } from '@/core/apiConfig';
import type { UserPermissionDto } from './mockupsShareDtos';
import type { Share, SharePermission } from './mockupsShareTypes';

const getDecoratorMockupsEndpoint = () => `${getApiAddress()}/decorator-mockups/v1`;

const parseResponse = (response: Response, parseJson = true) => {
  const { status } = response;

  if (status == 404) {
    return undefined;
  }

  if (status < 200 || status >= 400) {
    throw new Error(status.toString());
  }

  return parseJson ? response.json() : response;
};

/**
 * Creates or updates collection share permission.
 * @param id Collection ID.
 * @param permission Share permission.
 * @param email User email.
 * @param message Message for email.
 */
export const createOrUpdateCollectionShare = async (
  id: string,
  permission: SharePermission,
  email: string,
  message?: string,
) => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/${permission}`, {
      body: JSON.stringify({ email, message }),
      credentials: 'include',
      headers,
      method: 'PUT',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to share')) {
      log.error(error);
    }
  }
};

/**
 * Get collection permission for me.
 * @param id Collection ID.
 */
export const getMyCollectionPermission = async (
  id: string,
): Promise<UserPermissionDto | undefined> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/me`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Get collection shares.
 * @param id Collection ID.
 */
export const getCollectionShares = async (id: string): Promise<Share[]> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Delete existing collection share.
 * @param id Collection ID.
 * @param permission Share permission.
 * @param email User email.
 */
export const deleteCollectionShare = async (
  id: string,
  permission: SharePermission,
  email: string,
) => {
  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/collections/${id}/share/${permission}/${email}`, {
      credentials: 'include',
      method: 'DELETE',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to delete share')) {
      log.error(error);
    }
  }
};

/**
 * Creates or updates mockup share permission.
 * @param id Mockup ID.
 * @param permission Share permission.
 * @param email User email.
 * @param message Message for email.
 */
export const createOrUpdateMockupShare = async (
  id: string,
  permission: SharePermission,
  email: string,
  message?: string,
) => {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/${permission}`, {
      body: JSON.stringify({ email, message }),
      credentials: 'include',
      headers,
      method: 'PUT',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to share')) {
      log.error(error);
    }
  }
};

/**
 * Get mockup permission for me.
 * @param id Mockup ID.
 */
export const getMyMockupPermission = async (id: string): Promise<UserPermissionDto | undefined> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/me`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Get mockup shares.
 * @param id Mockup ID.
 */
export const getMockupShares = async (id: string): Promise<Share[]> => {
  try {
    const response = await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share`, {
      credentials: 'include',
    });
    return parseResponse(response);
  } catch (error) {
    log.error(error);
  }
  return undefined;
};

/**
 * Delete existing mockup share.
 * @param id Mockup ID.
 * @param permission Share permission.
 * @param email User email.
 */
export const deleteMockupShare = async (id: string, permission: SharePermission, email: string) => {
  try {
    await fetch(`${getDecoratorMockupsEndpoint()}/mockups/${id}/share/${permission}/${email}`, {
      credentials: 'include',
      method: 'DELETE',
    });
    // Clean liveblocks roomUsersInfoCache
    // LiveblocksService.roomUsersInfoCache = {};
  } catch (error) {
    if (!error.toString().includes('Failed to delete share')) {
      log.error(error);
    }
  }
};
